import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAdminAuth } from '../../auth/AdminAuthProvider';
import { buildPublicApiUrl } from '@shared/lib/publicApiUrl';

const API_BASE = import.meta.env.VITE_API_BASE || '';

export function AdminLoginPage() {
  const { accessDenied, clearAccessDenied } = useAdminAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const callbackError = searchParams.get('error');
    if (callbackError) {
      setError(callbackError);
      setSuccess(null);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (accessDenied) {
      setError('Access is restricted to the CMS administrator.');
    }
  }, [accessDenied]);

  const handleSendMagicLink = async () => {
    setError(null);
    setSuccess(null);
    clearAccessDenied();
    setLoading(true);

    try {
      const url = buildPublicApiUrl(API_BASE, '/api/cms/admin/magic-link');
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ redirectOrigin: window.location.origin }),
      });
      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(body.error ?? 'Unable to send sign-in link');
      }

      setSuccess(body.message ?? 'If you are authorized, check your email for a sign-in link.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to send sign-in link';
      setError(message);
      console.error('[error] [admin:login] Magic link request failed', { message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-base px-6 md:px-10">
      <div className="flex w-full max-w-sm flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h1 className="text-h3 text-text-primary md:text-h2">CMS Admin</h1>
          <p className="text-caption text-text-tertiary md:text-sm">
            Restricted area. Sign-in links are sent only to the CMS administrator.
          </p>
        </div>

        {error && (
          <p className="text-caption text-status-error">{error}</p>
        )}

        {success && (
          <p className="text-caption text-text-secondary">{success}</p>
        )}

        <button
          type="button"
          disabled={loading || Boolean(success)}
          onClick={handleSendMagicLink}
          className="btn-primary w-full disabled:opacity-50"
        >
          {loading ? 'Sending link…' : success ? 'Link sent' : 'Send administrator sign-in link'}
        </button>
      </div>
    </div>
  );
}
